package com.shadowings.gero.presentation.component

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.util.currentDateAsInt
import com.shadowings.gero.util.currentDateAsString
import com.shadowings.gero.util.dateIntToEpochDays
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronLeft
import compose.icons.feathericons.ChevronRight
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.cancel
import gero.composeapp.generated.resources.day_friday_short
import gero.composeapp.generated.resources.day_monday_short
import gero.composeapp.generated.resources.day_saturday_short
import gero.composeapp.generated.resources.day_sunday_short
import gero.composeapp.generated.resources.streak_restoration_confirm_btn
import gero.composeapp.generated.resources.streak_restoration_confirm_message
import gero.composeapp.generated.resources.streak_restoration_confirm_title
import gero.composeapp.generated.resources.streak_restoration_restorable_day_desc
import gero.composeapp.generated.resources.day_thursday_short
import gero.composeapp.generated.resources.day_tuesday_short
import gero.composeapp.generated.resources.day_wednesday_short
import gero.composeapp.generated.resources.month_april
import gero.composeapp.generated.resources.month_august
import gero.composeapp.generated.resources.month_december
import gero.composeapp.generated.resources.month_february
import gero.composeapp.generated.resources.month_january
import gero.composeapp.generated.resources.month_july
import gero.composeapp.generated.resources.month_june
import gero.composeapp.generated.resources.month_march
import gero.composeapp.generated.resources.month_may
import gero.composeapp.generated.resources.month_november
import gero.composeapp.generated.resources.month_october
import gero.composeapp.generated.resources.month_september
import gero.composeapp.generated.resources.next_month_desc
import gero.composeapp.generated.resources.previous_month_desc
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

@PreviewLightDark
@Composable
fun StreakMiniCalendarPreview() {
    val completedQuizDates = listOf(
        "2026-07-01",
        "2026-07-02",
        "2026-07-03",
        "2026-07-05",
        "2026-07-07",
        "2026-07-10",
        "2026-07-15",
        "2026-07-20",
        "2026-07-25"
    )

    AppTheme {
        Surface {
            StreakMiniCalendar(completedQuizDates = completedQuizDates)
        }
    }

}

@Composable
fun StreakMiniCalendar(
    completedQuizDates: List<String>,
    modifier: Modifier = Modifier,
    canRestoreStreak: Boolean = false,
    onRestoreDay: (String) -> Unit = {},
) {
    StreakMonthCalendar(
        completedQuizDates = completedQuizDates,
        modifier = modifier,
        canRestoreStreak = canRestoreStreak,
        onRestoreDay = onRestoreDay,
    )
}

@Composable
fun StreakMonthCalendar(
    completedQuizDates: List<String>,
    modifier: Modifier = Modifier,
    monthsBackCount: Int = 36,
    canRestoreStreak: Boolean = false,
    onRestoreDay: (String) -> Unit = {},
) {
    val completedSet = completedQuizDates.toSet()
    val todayDateInt = currentDateAsInt()
    val todayStr = currentDateAsString()
    val currentYear = todayDateInt / 10000
    val currentMonth = (todayDateInt % 10000) / 100

    val initialPage = monthsBackCount
    val pageCount = monthsBackCount + 1
    val pagerState = rememberPagerState(
        initialPage = initialPage,
        pageCount = { pageCount }
    )
    val coroutineScope = rememberCoroutineScope()
    val dayPendingConfirmation = remember { mutableStateOf<String?>(null) }

    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(20.dp))
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
            .padding(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        HorizontalPager(
            state = pagerState,
            modifier = Modifier.fillMaxWidth()
        ) { page ->
            val offsetMonths = page - initialPage
            val totalMonths = currentYear * 12 + (currentMonth - 1) + offsetMonths
            val year = totalMonths / 12
            val month = (totalMonths % 12) + 1

            MonthGridPage(
                year = year,
                month = month,
                completedSet = completedSet,
                todayStr = todayStr,
                isFirst = (page == 0),
                isLast = (page == pageCount - 1),
                canRestoreStreak = canRestoreStreak,
                onRestorableDayClick = { dateStr -> dayPendingConfirmation.value = dateStr },
                onPreviousMonth = {
                    coroutineScope.launch { pagerState.animateScrollToPage(page - 1) }
                },
                onNextMonth = {
                    coroutineScope.launch { pagerState.animateScrollToPage(page + 1) }
                }
            )
        }
    }

    dayPendingConfirmation.value?.let { dateStr ->
        StreakRestorationConfirmDialog(
            dateStr = dateStr,
            onConfirm = {
                onRestoreDay(dateStr)
                dayPendingConfirmation.value = null
            },
            onDismiss = { dayPendingConfirmation.value = null }
        )
    }
}

@Composable
private fun StreakRestorationConfirmDialog(
    dateStr: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(text = stringResource(Res.string.streak_restoration_confirm_title))
        },
        text = {
            Text(text = stringResource(Res.string.streak_restoration_confirm_message, dateStr))
        },
        confirmButton = {
            TextButton(onClick = onConfirm) {
                Text(text = stringResource(Res.string.streak_restoration_confirm_btn))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(text = stringResource(Res.string.cancel))
            }
        }
    )
}

private fun getDaysInMonth(year: Int, month: Int): Int {
    return when (month) {
        2 -> if ((year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)) 29 else 28
        4, 6, 9, 11 -> 30
        else -> 31
    }
}

@Composable
private fun getMonthName(month: Int): String {
    return when (month) {
        1 -> stringResource(Res.string.month_january)
        2 -> stringResource(Res.string.month_february)
        3 -> stringResource(Res.string.month_march)
        4 -> stringResource(Res.string.month_april)
        5 -> stringResource(Res.string.month_may)
        6 -> stringResource(Res.string.month_june)
        7 -> stringResource(Res.string.month_july)
        8 -> stringResource(Res.string.month_august)
        9 -> stringResource(Res.string.month_september)
        10 -> stringResource(Res.string.month_october)
        11 -> stringResource(Res.string.month_november)
        12 -> stringResource(Res.string.month_december)
        else -> ""
    }
}

@Composable
private fun MonthGridPage(
    year: Int,
    month: Int,
    completedSet: Set<String>,
    todayStr: String,
    isFirst: Boolean,
    isLast: Boolean,
    canRestoreStreak: Boolean = false,
    onRestorableDayClick: (String) -> Unit = {},
    onPreviousMonth: () -> Unit,
    onNextMonth: () -> Unit
) {
    val monthName = getMonthName(month)
    val daysInMonth = getDaysInMonth(year, month)
    val firstDayDateInt = year * 10000 + month * 100 + 1
    val firstDayEpoch = dateIntToEpochDays(firstDayDateInt)
    // Day of week: 1=Mon, 7=Sun
    val firstDayOfWeek = (firstDayEpoch + 3) % 7 + 1
    val leadingEmptyCells = firstDayOfWeek - 1

    val dayHeaders = listOf(
        stringResource(Res.string.day_monday_short),
        stringResource(Res.string.day_tuesday_short),
        stringResource(Res.string.day_wednesday_short),
        stringResource(Res.string.day_thursday_short),
        stringResource(Res.string.day_friday_short),
        stringResource(Res.string.day_saturday_short),
        stringResource(Res.string.day_sunday_short)
    )

    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Month Title Header
            Box(
                Modifier
                    .size(24.dp)
                    .then(if (!isFirst) Modifier.clickable(onClick = onPreviousMonth) else Modifier)
            ) {
                if (!isFirst) {
                    Icon(
                        imageVector = FeatherIcons.ChevronLeft,
                        contentDescription = stringResource(Res.string.previous_month_desc),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f),
                        modifier = Modifier.size(24.dp)
                    )
                }
            }
            Text(
                text = "$monthName $year",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
                modifier = Modifier.weight(1f).padding(bottom = 8.dp)
            )
            Box(
                Modifier
                    .size(24.dp)
                    .then(if (!isLast) Modifier.clickable(onClick = onNextMonth) else Modifier)
            ) {
                if (!isLast) {
                    Icon(
                        imageVector = FeatherIcons.ChevronRight,
                        contentDescription = stringResource(Res.string.next_month_desc),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f),
                        modifier = Modifier.size(24.dp)
                    )
                }
            }
        }

        // Day of week header
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceEvenly
        ) {
            dayHeaders.forEach { header ->
                Box(
                    modifier = Modifier.size(36.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = header,
                        style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold),
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f),
                        textAlign = TextAlign.Center
                    )
                }
            }
        }

        Spacer(Modifier.height(4.dp))

        // Always render the maximum possible number of weeks a month can span (a 31-day
        // month starting on a Sunday needs 6), padded with blank trailing cells, so every
        // page has the same height and swiping between months doesn't resize the calendar.
        val rows = 6

        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            for (rowIndex in 0 until rows) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceEvenly,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    for (colIndex in 0 until 7) {
                        val cellIndex = rowIndex * 7 + colIndex
                        val dayNumber = cellIndex - leadingEmptyCells + 1

                        if (dayNumber in 1..daysInMonth) {
                            val mStr = if (month < 10) "0$month" else "$month"
                            val dStr = if (dayNumber < 10) "0$dayNumber" else "$dayNumber"
                            val dateStr = "$year-$mStr-$dStr"
                            val isCompleted = completedSet.contains(dateStr)
                            val isToday = (dateStr == todayStr)
                            val isRestorable = canRestoreStreak && !isCompleted && dateStr < todayStr

                            MonthDayItem(
                                dayNumber = dayNumber,
                                isCompleted = isCompleted,
                                isToday = isToday,
                                isRestorable = isRestorable,
                                onClick = if (isRestorable) {
                                    { onRestorableDayClick(dateStr) }
                                } else null
                            )
                        } else {
                            // Blank cell
                            Box(modifier = Modifier.size(36.dp))
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MonthDayItem(
    dayNumber: Int,
    isCompleted: Boolean,
    isToday: Boolean,
    isRestorable: Boolean = false,
    onClick: (() -> Unit)? = null,
) {
    val backgroundColor = when {
        isCompleted -> MaterialTheme.colorScheme.primaryContainer
        isToday -> MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.4f)
        else -> MaterialTheme.colorScheme.surface
    }

    val borderColor = when {
        isToday && !isCompleted -> MaterialTheme.colorScheme.error
        isCompleted -> MaterialTheme.colorScheme.primary
        isRestorable -> MaterialTheme.colorScheme.tertiary
        else -> Color.Transparent
    }

    val textColor = when {
        isCompleted -> MaterialTheme.colorScheme.onPrimaryContainer
        isToday -> MaterialTheme.colorScheme.error
        isRestorable -> MaterialTheme.colorScheme.tertiary
        else -> MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
    }

    val restorableDayDescription = if (onClick != null) {
        stringResource(Res.string.streak_restoration_restorable_day_desc)
    } else {
        null
    }

    Box(
        modifier = Modifier
            .size(36.dp)
            .clip(CircleShape)
            .background(backgroundColor)
            .then(
                if (borderColor != Color.Transparent) {
                    Modifier.border(1.5.dp, borderColor, CircleShape)
                } else Modifier
            )
            .then(
                if (onClick != null) {
                    Modifier
                        .clickable(onClick = onClick)
                        .semantics { contentDescription = restorableDayDescription.orEmpty() }
                } else Modifier
            ),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = dayNumber.toString(),
            fontSize = 12.sp,
            fontWeight = if (isToday || isRestorable) FontWeight.Bold else FontWeight.Normal,
            color = textColor,
            textAlign = TextAlign.Center
        )
    }
}
